import React, { useEffect, useState } from "react";
import { FaTrashAlt, FaComment, FaCheck } from "react-icons/fa";
import ShowModal from "./ShowModal";
import DosePackageComment from "../../pages/HUD/patients/dose_package_comment";
import { BsChatDotsFill, BsPencilFill } from "react-icons/bs";
import { toast } from "react-toastify";
import ConfirmModal from "../popups/confirmationModal/ConfirmModal";
import { RiCheckDoubleLine, RiDeleteBin6Line } from "react-icons/ri";
import { deleteHudPatientPackageDose, patchHudPatientPackageDose } from "../../services/hud/hud_doses";
import AddUpdateDose from "../../pages/HUD/patients/add_update_dose";
import NewDosePackageComment from "../../pages/New_Clinic_Dashboard/patients/new_dose_package_comment";
import TreatmentElapsedTimer, { isTreatmentRunning } from "./TreatmentElapsedTimer";

const formatTime = (value) => {
    const date = new Date(value);
    if (isNaN(date.getTime())) return value;
    return date.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
};

const formatDuration = (start, end) => {
    if (!start || !end) return null;
    const ms = new Date(end).getTime() - new Date(start).getTime();
    if (isNaN(ms) || ms < 0) return null;
    const totalSeconds = Math.round(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
};

const statusTextColor = (status) => {
    if (status === "Completed") return "text-green-600";
    if (status === "In Progress") return "text-yellow-600";
    return "text-gray-500";
};

// showLastDoseTreatment: TPT packages on a new TPT machine — shows status + start/end time for the last dose.
const DoseTable = ({ data, packageId, fetchData, needExtraOptions = true, isNewClinic = false, showLastDoseTreatment = false }) => {
    const [modalIsOpen, setIsOpen] = useState(false);
    const [openingPackageDose, setOpeningPackageDose] = useState({});
    const [modalIsOpenPackage, setIsOpenPackage] = useState(false);

    const totalColumns = 18;
    const doseNumbers = Array.from({ length: totalColumns }, (_, i) => i + 1);
    const lastDoseNumber = Array.isArray(data) ? data.length : 0;

    const getDoseData = (doseNumber, key) => {
        const dose = data[doseNumber - 1];
        if (!dose) return "";
        // if (key === "comments") {
        //     return dose.dose_comments && dose.dose_comments.length > 0
        //         ? dose.dose_comments.map((comment) => comment.comment).join(", ")
        //         : "";
        // }
        return dose[key] || "";
    };

    const closeAddPackageModal = () => {
        setIsOpenPackage(false);
        setOpeningPackageDose({});
    };


    const openModalAddPackage = (item) => {
        setOpeningPackageDose(item);
        setIsOpenPackage(true);
    };

    // Open modal with the selected PDF ID
    const openModal = (item) => {
        setOpeningPackageDose(item);
        setIsOpen(true);
    };

    // Close modal
    const closeModal = () => {
        setIsOpen(false);
        setOpeningPackageDose({});
    };

    const handleCommentAdded = () => {
        closeModal();
        fetchData();
    };

    const [modalIsDeleteOpen, setIsDeleteOpen] = useState(false);
    const [isConfirmDelete, setIsConfirmDelete] = useState(false);
    const [doseId, setDoseId] = useState(null);

    // Close modal
    const closeDeleteModal = () => {
        setIsDeleteOpen(false);
        setIsConfirmDelete(false);
        setDoseId(null);
    };

    // Open modal with the selected PDF ID
    const openDeleteModal = (id) => {
        setDoseId(id);
        setIsDeleteOpen(true);
    };

    // Delete PDF
    const handleDelete = async () => {
        if (!doseId) return;
        try {
            const response = await deleteHudPatientPackageDose(packageId, doseId);
            if (response.status === 204) {
                fetchData();
                toast.success("Dose deleted successfully");
            } else {
                toast.error("Unable to delete dose");
            }
        } catch (error) {
            console.error('Error:', error);
            toast.error('Failed to delete dose');
        } finally {
            closeModal();
        }
    };

    useEffect(() => {
        if (isConfirmDelete) {
            handleDelete();
        }
    }, [isConfirmDelete]);

    const [modalIsCompleteOpen, setIsCompleteOpen] = useState(false);
    const [isConfirmComplete, setIsConfirmComplete] = useState(false);

    // Close modal
    const closeCompleteModal = () => {
        setIsCompleteOpen(false);
        setIsConfirmComplete(false);
        setDoseId(null);
    };

    // Open modal with the selected PDF ID
    const openCompleteModal = (id) => {
        setDoseId(id);
        setIsCompleteOpen(true);
    };

    const handleComplete = async () => {
        if (!doseId) return;
        try {
            const response = await patchHudPatientPackageDose(packageId, doseId, { "status": "Completed" });
            if (response.status === 200) {
                fetchData();
                toast.success("Dose Updated successfully");
            } else {
                toast.error("Unable to update dose");
            }
        } catch (error) {
            console.error('Error:', error);
            toast.error('Failed to update dose');
        } finally {
            closeModal();
        }
    };

    useEffect(() => {
        if (isConfirmComplete) {
            handleComplete();
        }
    }, [isConfirmComplete]);

    return (
        <table className="table-auto border-collapse w-full text-[0.6rem]">
            <thead>
                <tr>
                    <th className="bg-primary text-white border font-bold">Dose</th>
                    {doseNumbers.map((num, index) => (
                        <th
                            key={num}
                            title={num}
                            className={`bg-primary text-white border font-bold ${!getDoseData(num, isNewClinic ? "joulepercm" : "j_cm2") ? "p-0.5" : "p-1"
                                }`}
                        >
                            {num}
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {/* J/CM² Row */}
                <tr>
                    <td className="border font-bold">J/CM²</td>
                    {doseNumbers.map((num) => (
                        <td key={num} className={`border ${!getDoseData(num, isNewClinic ? "joulepercm" : "j_cm2") ? "p-0.5" : "p-1"}`}>
                            {getDoseData(num, isNewClinic ? "joulepercm" : "j_cm2")}
                        </td>
                    ))}
                </tr>
                {/* Status Row */}
                {
                    isNewClinic ? <></> :
                        <tr>
                            <td className="border font-bold">Status</td>
                            {doseNumbers.map((num) => (
                                <td
                                    key={num}
                                    className={`border ${getDoseData(num, "status") === "Completed"
                                        ? "text-green-500 font-bold"
                                        : getDoseData(num, "status") === "Not Started"
                                            ? "text-gray-500 font-bold"
                                            : ""
                                        } ${!getDoseData(num, isNewClinic ? "joulepercm" : "j_cm2") ? "p-0.5" : "p-1"}`}
                                >
                                    {getDoseData(num, "status")}
                                </td>
                            ))}
                        </tr>
                }
                {/* Last dose treatment Status + Time rows (new TPT machine only) */}
                {
                    showLastDoseTreatment ?
                        <>
                            <tr>
                                <td className="border font-bold">Status</td>
                                {doseNumbers.map((num) => (
                                    <td
                                        key={num}
                                        className={`border font-bold ${num === lastDoseNumber ? `p-1 ${statusTextColor(getDoseData(num, "status"))}` : "p-0.5"}`}
                                    >
                                        {num === lastDoseNumber ? (getDoseData(num, "status") || "Not Started") : ""}
                                    </td>
                                ))}
                            </tr>
                            <tr>
                                <td className="border font-bold">Time</td>
                                {doseNumbers.map((num) => (
                                    <td key={num} className={`border ${num === lastDoseNumber ? "p-1 whitespace-nowrap" : "p-0.5"}`}>
                                        {num === lastDoseNumber ? (
                                            getDoseData(num, "open_time") ? (
                                                <>
                                                    <div>Start: {formatTime(getDoseData(num, "open_time"))}</div>
                                                    {isTreatmentRunning(data[num - 1]) ? (
                                                        <TreatmentElapsedTimer startTime={getDoseData(num, "open_time")} compact />
                                                    ) : (
                                                        <div>End: {getDoseData(num, "close_time") ? formatTime(getDoseData(num, "close_time")) : "—"}</div>
                                                    )}
                                                    {formatDuration(getDoseData(num, "open_time"), getDoseData(num, "close_time")) ? (
                                                        <div className="font-bold">{formatDuration(getDoseData(num, "open_time"), getDoseData(num, "close_time"))}</div>
                                                    ) : null}
                                                </>
                                            ) : "—"
                                        ) : ""}
                                    </td>
                                ))}
                            </tr>
                        </> : <></>
                }
                {/* Tube Row */}
                <tr>
                    <td className="border font-bold">Tube</td>
                    {doseNumbers.map((num) => (
                        <td key={num} className={`border ${!getDoseData(num, isNewClinic ? "tube" : "tube") ? "p-0.5" : "p-1"}`}>
                            {getDoseData(num, "tube")}
                        </td>
                    ))}
                </tr>
                {/* Date Row */}
                <tr>
                    <td className="border font-bold">Date</td>
                    {doseNumbers.map((num) => (
                        <td key={num} className={`border ${!getDoseData(num, "date") ? "p-0.5" : "p-1"}`}>
                            {getDoseData(num, "date")}
                        </td>
                    ))}
                </tr>
                {/* Edit Row */}
                {
                    needExtraOptions ?
                        <tr>
                            <td className="border font-bold">Edit</td>
                            {doseNumbers.map((num) => (
                                <td
                                    key={num}
                                    className="border text-green-800 cursor-pointer"
                                    onClick={() => openModalAddPackage(data[num - 1])}
                                >
                                    {getDoseData(num, "status") === "Not Started" ?
                                        <div className="w-14 self-center justify-center flex flex-row">
                                            <BsPencilFill size={16} />
                                        </div>
                                        : ""}
                                </td>
                            ))}
                        </tr> : <></>
                }
                {/* Comment Row */}
                <tr>
                    <td className="border font-bold p-0.5">Comment</td>
                    {doseNumbers.map((num) => (
                        <td
                            key={num}
                            className="border cursor-pointer p-0.5 max-w-28"
                            onClick={getDoseData(num, "status") === "Not Started" ? () => openModal(data[num - 1]) : null}
                        >
                            {data[num - 1] && data[num - 1].last_comment ? getDoseData(num, "j_cm2") ? (
                                <div
                                    className={`w-full font-bold ${getDoseData(num, "status") === "Not Started" ? "text-blue-600" : "text-gray-500"}`}
                                    style={{ wordBreak: "break-word", whiteSpace: "pre-wrap", }}  // Wrap text
                                >
                                    {((data[num - 1].last_comment || data[num - 1].last_dose_comment) || "").length > 200
                                        ? (
                                            <>
                                                {data[num - 1].last_comment?.slice(0, 500) || data[num - 1].last_dose_comment?.slice(0, 500)}
                                                <br />
                                                {data[num - 1].last_comment?.slice(500) || data[num - 1].last_dose_comment?.slice(500)}
                                            </>
                                        )
                                        : (
                                            data[num - 1].last_comment || data[num - 1].last_dose_comment ?
                                                data[num - 1].last_comment ?? data[num - 1].last_dose_comment :
                                                data[num - 1].last_comment || data[num - 1].last_dose_comment || getDoseData(num, "status") === "Not Started" ? <BsChatDotsFill size={16} /> : <></>
                                        )
                                    }
                                </div>
                            ) : "" : <div
                                className={`w-full font-bold ${getDoseData(num, "status") === "Not Started" ? "text-blue-600" : "text-gray-500"}`}
                                style={{ wordBreak: "break-word", whiteSpace: "pre-wrap", }}  // Wrap text
                            >
                                {
                                    <>
                                        {data[num - 1]?.notes?.slice(0, 500) || data[num - 1]?.notes?.slice(0, 500)}
                                        <br />
                                        {data[num - 1]?.notes?.slice(500) || data[num - 1]?.notes?.slice(500)}
                                    </>
                                }
                            </div>}
                        </td>
                    ))}
                </tr>
                {/* Tablets Row */}
                <tr>
                    <td className="border font-bold p-0.5">Tablets</td>
                    {doseNumbers.map((num) => (
                        <td key={num} className="border p-0.5">
                            {getDoseData(num, "tablets") !== undefined && getDoseData(num, "tablets") !== null && getDoseData(num, "tablets") !== "" ? getDoseData(num, "tablets") : "-"}
                        </td>
                    ))}
                </tr>
                {/* Delete Row */}
                {
                    needExtraOptions ?

                        <tr>
                            <td className="border font-bold">Delete</td>
                            {doseNumbers.map((num) => (
                                <td
                                    key={num}
                                    className="border text-red-500 cursor-pointer p-0.5 justify-center"
                                    onClick={getDoseData(num, "status") === "Not Started" ? () => openDeleteModal(getDoseData(num, "dose_id")) : null}
                                >
                                    {getDoseData(num, "status") === "Not Started" ?
                                        <div className="w-14 self-center justify-center flex flex-row">
                                            <FaTrashAlt size={16} />
                                        </div>
                                        : ""}
                                </td>
                            ))}
                        </tr> : <></>
                }
                {/* Complete Row */}
                {
                    needExtraOptions ?
                        <tr>
                            <td className="border p-2 font-bold">Complete</td>
                            {doseNumbers.map((num) => (
                                <td
                                    key={num}
                                    className="border text-primary cursor-pointer p-2"
                                    onClick={getDoseData(num, "status") === "Not Started" ? () => openCompleteModal(getDoseData(num, "dose_id")) : null}
                                >
                                    {getDoseData(num, "status") === "Not Started" ?
                                        <div className="w-14 self-center justify-center flex flex-row">
                                            <FaCheck size={16} />
                                        </div>
                                        : ""}
                                </td>
                            ))}
                        </tr> : <></>
                }
            </tbody>
            <ShowModal contentLabel={"Comment alert"} isOpen={modalIsOpen} modalWidth="50%" onRequestClose={closeModal} componentToPass={isNewClinic ? <NewDosePackageComment doseId={openingPackageDose.dose_id} onAddNewComment={handleCommentAdded} notes={openingPackageDose.notes} /> : <DosePackageComment packageId={packageId} doseId={openingPackageDose.dose_id} onAddNewComment={handleCommentAdded} doseComments={openingPackageDose.dose_comments} />} />
            <ShowModal contentLabel={"Add dose alert"} isOpen={modalIsOpenPackage} modalWidth={"50%"} onRequestClose={closeAddPackageModal} componentToPass={<AddUpdateDose initialValues={openingPackageDose} packageId={packageId} onDoseOperation={() => { fetchData(); closeAddPackageModal() }} />} />
            <ConfirmModal
                isOpen={modalIsDeleteOpen}
                onRequestClose={closeDeleteModal}
                contentLabel="Delete Confirmation"
                onClose={closeDeleteModal}
                setIsConfirmDelete={setIsConfirmDelete}
                title="Are you sure you want to delete this dose?"
                icon={
                    <RiDeleteBin6Line color="red" size={60} />
                }
            />
            <ConfirmModal
                isOpen={modalIsCompleteOpen}
                onRequestClose={closeCompleteModal}
                contentLabel="Complete Confirmation"
                onClose={closeCompleteModal}
                setIsConfirmDelete={setIsConfirmComplete}
                title="Are you sure you want to complete this dose?"
                icon={
                    <RiCheckDoubleLine color="red" size={60} />
                }
            />
        </table>
    );
};

export default DoseTable;
